import { forwardRef, type HTMLAttributes } from "react";
import { Flex } from "src/components/Flex.tsx";
import { css } from "@emotion/react";

import type { ExactTheme } from "src/lib/emotion/theme.ts";
import type { ReactNode } from "react";

export type ContextItemModel = {
  label: ReactNode;
  icon: ReactNode;
  danger?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export const DataGridContextMenu = forwardRef<
  HTMLDivElement,
  {
    items: ContextItemModel[];
    customContent?: ReactNode;
    onClose?: () => void;
  }
>(({ items, customContent, onClose, ...rest }, ref) => {
  return (
    <div css={contentStyles} {...rest} ref={ref}>
      {customContent || (
        <Flex flexDirection={"column"}>
          {items.map((item, i) => (
            <Item key={i} {...item} />
          ))}
        </Flex>
      )}
    </div>
  );
});

const itemStyles = (theme: ExactTheme) => css`
  padding: 10px;
  display: flex;
  align-items: center;
  font-size: 14px;
  border-bottom: 1px solid ${theme.colors.border.muted};
  :first-of-type {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }
  :last-of-type {
    border-bottom: none;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  :hover {
    background-color: ${theme.colorsV2.grey[50]};
  }
  cursor: pointer;
`;

const Item = (
  props: {
    label: ReactNode;
    icon: ReactNode;
    danger?: boolean;
    safe?: boolean;
  } & HTMLAttributes<HTMLDivElement>,
) => {
  const { danger, icon, label = false, ...rest } = props;
  return (
    <div {...rest} css={itemStyles}>
      <div css={itemIconStyles({ danger })}>{icon}</div>
      <div css={itemLabelStyles}>{label}</div>
    </div>
  );
};

const itemIconStyles =
  ({ danger = false }: { danger: boolean | undefined }) =>
  (theme: ExactTheme) => css`
    font-size: 14px;
    color: ${danger ? theme.colorsV2.red[500] : theme.colors.blue[500]};
  `;

const itemLabelStyles = (theme: ExactTheme) => css`
  margin-left: 10px;
  color: ${theme.colorsV2.grey[800]};
`;

const contentStyles = (theme: ExactTheme) => css`
  position: relative;
  border-radius: 6px;
  background: ${theme.colorsV2.white};
  z-index: ${theme.zIndices.tooltips};
  outline: none;
  box-shadow: ${theme.shadowV2.default};
`;
